import api from './api';
import {
  AccrualType,
  VacationBalance,
  VacationPolicy,
  VacationRequest,
  VacationRequestStatus,
  VacationUsageSummary,
} from '../types';

export interface VacationFilters {
  status?: VacationRequestStatus | string;
  memberName?: string;
  memberId?: string;
  page?: number;
  limit?: number;
}

export interface PaginatedVacationRequests {
  requests: VacationRequest[];
  total: number;
  page: number;
  totalPages: number;
}

export interface PaginatedUsageSummaries {
  summaries: VacationUsageSummary[];
  total: number;
  page: number;
  totalPages: number;
}

export interface CreateVacationRequestDto {
  memberId: string;
  startDate: string;
  endDate: string;
  notes?: string;
}

export interface ReviewVacationRequestDto {
  comments?: string;
}

export interface CancelVacationRequestDto {
  reason?: string;
}

export interface UpdateVacationBalanceDto {
  annualAllowance?: number;
  carriedOver?: number;
  accrued?: number;
}

export interface CreateVacationPolicyDto {
  countryCode: string;
  name: string;
  annualAllowance: number;
  accrualType: AccrualType;
  carryOverLimit?: number;
  maxConsecutiveDays?: number;
  minConsecutiveDays?: number;
  advanceNoticeDays?: number;
  probationPeriodDays?: number;
}

export interface UpdateVacationPolicyDto {
  name?: string;
  annualAllowance?: number;
  accrualType?: AccrualType;
  carryOverLimit?: number;
  maxConsecutiveDays?: number;
  minConsecutiveDays?: number;
  advanceNoticeDays?: number;
  probationPeriodDays?: number;
}

interface SpringPage<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
}

/** Type guard for Spring Boot Page<T> responses. */
const isSpringPage = <T>(data: unknown): data is SpringPage<T> =>
  typeof data === 'object' && data !== null && 'content' in data;

/** Shape of an Axios-style rejected error we care about. */
interface HttpErrorShape {
  response?: { status?: number };
}

/** Extracts the HTTP status from an unknown rejected error, if present. */
const getErrorStatus = (error: unknown): number | undefined => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    return (error as HttpErrorShape).response?.status;
  }
  return undefined;
};

/**
 * Vacation Service
 * Centralizes all vacation-related API calls (requests, balances, policies,
 * and usage reporting). Paths are relative to the axios baseURL which already
 * ends in `/api`.
 */
class VacationService {
  private baseUrl = '/vacations';

  private buildPageParams(filters?: { page?: number; limit?: number }): URLSearchParams {
    const params = new URLSearchParams();
    if (filters?.page) {
      // Spring Boot uses 0-indexed pages
      params.append('page', (filters.page - 1).toString());
    }
    if (filters?.limit) {
      params.append('size', filters.limit.toString());
    }
    return params;
  }

  private unwrapRequests(data: unknown, fallbackPage: number): PaginatedVacationRequests {
    if (isSpringPage<VacationRequest>(data)) {
      return {
        requests: data.content,
        total: data.totalElements,
        page: data.number + 1,
        totalPages: data.totalPages,
      };
    }
    const requests = (data as VacationRequest[]) ?? [];
    return { requests, total: requests.length, page: fallbackPage, totalPages: 1 };
  }

  /**
   * Get all vacation requests (scoped server-side by role: members see their
   * own, managers see their reports, admins see the whole org).
   */
  async getRequests(filters?: VacationFilters): Promise<PaginatedVacationRequests> {
    const params = this.buildPageParams(filters);
    if (filters?.status) params.append('status', filters.status);
    const search = filters?.memberName?.trim();
    if (search) params.append('search', search);
    const url = params.toString()
      ? `${this.baseUrl}/requests?${params.toString()}`
      : `${this.baseUrl}/requests`;
    const response = await api.get(url);
    return this.unwrapRequests(response.data, filters?.page ?? 1);
  }

  /** Get vacation requests for a specific member. */
  async getMyRequests(memberId: string, filters?: { page?: number; limit?: number }): Promise<PaginatedVacationRequests> {
    const params = this.buildPageParams(filters);
    const url = params.toString()
      ? `${this.baseUrl}/requests/member/${memberId}?${params.toString()}`
      : `${this.baseUrl}/requests/member/${memberId}`;
    const response = await api.get(url);
    return this.unwrapRequests(response.data, filters?.page ?? 1);
  }

  /** Get a single vacation request by ID. */
  async getRequestById(id: string): Promise<VacationRequest> {
    const response = await api.get<VacationRequest>(`${this.baseUrl}/requests/${id}`);
    return response.data;
  }

  /** Get the pending-approval queue for a manager. */
  async getPendingForManager(managerId: string, filters?: { page?: number; limit?: number }): Promise<PaginatedVacationRequests> {
    const params = this.buildPageParams(filters);
    const url = params.toString()
      ? `${this.baseUrl}/requests/pending/manager/${managerId}?${params.toString()}`
      : `${this.baseUrl}/requests/pending/manager/${managerId}`;
    const response = await api.get(url);
    return this.unwrapRequests(response.data, filters?.page ?? 1);
  }

  /** Create a new vacation request. */
  async createRequest(data: CreateVacationRequestDto): Promise<VacationRequest> {
    const response = await api.post<VacationRequest>(`${this.baseUrl}/requests`, data);
    return response.data;
  }

  /** Approve a vacation request. */
  async approveRequest(id: string, data: ReviewVacationRequestDto = {}): Promise<VacationRequest> {
    const response = await api.post<VacationRequest>(`${this.baseUrl}/requests/${id}/approve`, data);
    return response.data;
  }

  /** Reject a vacation request. */
  async rejectRequest(id: string, data: ReviewVacationRequestDto = {}): Promise<VacationRequest> {
    const response = await api.post<VacationRequest>(`${this.baseUrl}/requests/${id}/reject`, data);
    return response.data;
  }

  /** Cancel a vacation request. */
  async cancelRequest(id: string, data: CancelVacationRequestDto = {}): Promise<VacationRequest> {
    const response = await api.post<VacationRequest>(`${this.baseUrl}/requests/${id}/cancel`, data);
    return response.data;
  }

  /**
   * Get a member's balance for a given year. The backend returns a paged
   * collection of balances; we unwrap it and return the matching year (or the
   * first entry) so callers get a single balance.
   */
  async getBalance(memberId: string, year: number): Promise<VacationBalance | null> {
    try {
      const response = await api.get(`${this.baseUrl}/balances/member/${memberId}?year=${year}`);
      const data = response.data;
      const balances: VacationBalance[] = isSpringPage<VacationBalance>(data)
        ? data.content
        : ((data as VacationBalance[]) ?? []);
      if (balances.length === 0) return null;
      return balances.find((b) => b.year === year) ?? balances[0];
    } catch (error) {
      // Balances are created lazily (on first request or explicit admin init), so
      // the backend returns 404 when no row exists for the year. Treat that as
      // "no balance yet" rather than an error, so React Query doesn't retry and
      // flood the console with 404s; the UI shows its empty state instead.
      if (getErrorStatus(error) === 404) {
        return null;
      }
      throw error;
    }
  }

  /** Admin: override a member's balance for a given year. */
  async updateBalance(memberId: string, year: number, data: UpdateVacationBalanceDto): Promise<VacationBalance> {
    const response = await api.patch<VacationBalance>(
      `${this.baseUrl}/balances/member/${memberId}/year/${year}`,
      data,
    );
    return response.data;
  }

  /**
   * Admin: create (initialize) a member's balance for a given year. The backend
   * endpoint is idempotent, so calling it when a balance already exists is safe.
   */
  async initializeBalance(memberId: string, year: number): Promise<VacationBalance> {
    const response = await api.post<VacationBalance>(
      `${this.baseUrl}/balances/member/${memberId}/year/${year}`,
    );
    return response.data;
  }

  /**
   * Admin: update-or-insert a member's balance for a given year. Tries a PATCH
   * first; if the balance row doesn't exist yet (404), initializes it and
   * retries the PATCH so the admin screen works whether or not a balance
   * already exists. Non-404 errors are rethrown untouched.
   */
  async upsertBalance(memberId: string, year: number, data: UpdateVacationBalanceDto): Promise<VacationBalance> {
    try {
      return await this.updateBalance(memberId, year, data);
    } catch (error) {
      if (getErrorStatus(error) === 404) {
        await this.initializeBalance(memberId, year);
        return this.updateBalance(memberId, year, data);
      }
      throw error;
    }
  }

  /** Manager/Admin: get the team usage summary (entitlement/used/remaining). */
  async getUsageSummary(filters?: {
    year?: number;
    accrualType?: AccrualType;
    memberId?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<PaginatedUsageSummaries> {
    const params = this.buildPageParams(filters);
    if (filters?.year) params.append('year', filters.year.toString());
    if (filters?.accrualType) params.append('accrualType', filters.accrualType);
    if (filters?.memberId) params.append('memberId', filters.memberId);
    const search = filters?.search?.trim();
    if (search) params.append('search', search);

    const url = params.toString()
      ? `${this.baseUrl}/usage-summary?${params.toString()}`
      : `${this.baseUrl}/usage-summary`;
    const response = await api.get(url);
    const data = response.data;

    if (isSpringPage<VacationUsageSummary>(data)) {
      return {
        summaries: data.content,
        total: data.totalElements,
        page: data.number + 1,
        totalPages: data.totalPages,
      };
    }
    const summaries = (data as VacationUsageSummary[]) ?? [];
    return { summaries, total: summaries.length, page: filters?.page ?? 1, totalPages: 1 };
  }

  /** Get all per-country vacation policies. */
  async getPolicies(filters?: { page?: number; limit?: number }): Promise<VacationPolicy[]> {
    const params = this.buildPageParams({ page: filters?.page, limit: filters?.limit ?? 200 });
    const response = await api.get(`${this.baseUrl}/policies?${params.toString()}`);
    const data = response.data;
    if (isSpringPage<VacationPolicy>(data)) {
      return data.content;
    }
    return (data as VacationPolicy[]) ?? [];
  }

  /** Admin: create a per-country vacation policy. */
  async createPolicy(data: CreateVacationPolicyDto): Promise<VacationPolicy> {
    const response = await api.post<VacationPolicy>(`${this.baseUrl}/policies`, data);
    return response.data;
  }

  /** Admin: update a per-country vacation policy. */
  async updatePolicy(id: string, data: UpdateVacationPolicyDto): Promise<VacationPolicy> {
    const response = await api.patch<VacationPolicy>(`${this.baseUrl}/policies/${id}`, data);
    return response.data;
  }

  /** Admin: delete a per-country vacation policy. */
  async deletePolicy(id: string): Promise<void> {
    await api.delete(`${this.baseUrl}/policies/${id}`);
  }
}

// Export singleton instance
export const vacationService = new VacationService();
